import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { usePackagedAppsSummary } from 'src/hooks/wdac';
import {
  PACKAGED_APP_STATUS,
  getPackagedAppStatusColor,
  getPackagedAppStatusLabel,
} from 'src/utils/enums/packagedAppStatus';
import {
  CardTitle,
  ChartTooltipBox,
  EmptyChartState,
  OVERVIEW_CARD_SX as CARD_SX,
} from 'src/features/charts/OverviewCard';
import { formatCount } from 'src/utils/formatNumber.util';
import PackagedAppsPolicyCard from './PackagedAppsPolicyCard';

const CARD_COLUMN_SX = { ...CARD_SX, flex: '1 1 0', minWidth: 300 };

// Three bars and nothing else, so the card never drives the height of the row it sits in. The plot
// area is CHART_HEIGHT less the margins, split evenly across the bars, so the height and BAR_SIZE
// together set how much air sits between them.
const CHART_HEIGHT = 70;
const BAR_SIZE = 14;

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }
  const item = payload[0]?.payload;
  return (
    <ChartTooltipBox>
      {item?.label}: {formatCount(item?.value)}
    </ChartTooltipBox>
  );
};

const TrustStatusCard = ({ data, isLoading, selectedStatus, onStatusClick }) => (
  <Stack gap={1} sx={CARD_COLUMN_SX}>
    <CardTitle title="Trust Status" subtitle="All packaged apps · click a bar to filter" />
    {isLoading ? (
      <Skeleton variant="rounded" height={CHART_HEIGHT} sx={{ bgcolor: '#4B4B4B' }} />
    ) : data.some(item => item.value > 0) ? (
      <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 2, right: 40, left: 0, bottom: 2 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.16)"
              horizontal={false}
            />
            <XAxis type="number" allowDecimals={false} hide height={0} />
            <YAxis
              type="category"
              dataKey="label"
              width={84}
              interval={0}
              tickMargin={8}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#F3F4F6' }}
            />
            <Bar
              dataKey="value"
              barSize={BAR_SIZE}
              radius={[0, 4, 4, 0]}
              cursor="pointer"
              onClick={item => onStatusClick(item?.status || item?.payload?.status)}>
              {data.map(item => {
                const selected = selectedStatus === item.status;
                return (
                  <Cell
                    key={item.status}
                    fill={item.color}
                    stroke={selected ? '#7DFF92' : 'transparent'}
                    strokeWidth={selected ? 2 : 0}
                    opacity={selectedStatus && !selected ? 0.45 : 1}
                  />
                );
              })}
              <LabelList
                dataKey="value"
                position="right"
                formatter={formatCount}
                style={{
                  fill: '#F3F4F6',
                  fontSize: 10,
                  fontWeight: 600,
                  // LabelList spreads the bar sector's props onto the text, so the selected Cell's
                  // stroke would otherwise outline the count in green.
                  stroke: 'none',
                  strokeWidth: 0,
                }}
              />
            </Bar>
            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.06)' }} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    ) : (
      <EmptyChartState height={CHART_HEIGHT} message="No packaged apps reported yet." />
    )}
  </Stack>
);

const PackagedAppsOverview = ({ trustStatus, onTrustStatusChange }) => {
  const summaryQuery = usePackagedAppsSummary({ apiQuery: {} });

  const countByStatus = new Map(
    (summaryQuery.data?.statusCounts || []).map(item => [item.status, item.count]),
  );
  const statusData = Object.values(PACKAGED_APP_STATUS).map(status => ({
    status,
    label: getPackagedAppStatusLabel(status),
    value: countByStatus.get(status) || 0,
    color: getPackagedAppStatusColor(status),
  }));

  const onStatusClick = status => {
    if (!status) {
      return;
    }
    onTrustStatusChange(trustStatus === status ? '' : status);
  };

  return (
    <Stack gap={1.5} sx={{ width: '100%' }}>
      <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.4 }}>
        Packaged Apps Overview
      </Typography>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={1.5}
        alignItems="stretch"
        sx={{ width: '100%' }}>
        <TrustStatusCard
          data={statusData}
          isLoading={summaryQuery.isLoading}
          selectedStatus={trustStatus}
          onStatusClick={onStatusClick}
        />
        <PackagedAppsPolicyCard sx={{ flex: '1 1 0', minWidth: 300 }} />
      </Stack>
    </Stack>
  );
};

export default PackagedAppsOverview;
